import { ResolvedColorScheme } from '@inway/meteor-layout-helper';
import { useCallback, useDebugValue, useLayoutEffect, useState } from 'react';
import usePreferredColorScheme from './usePreferredColorScheme';

/**
 * React hook to manage color scheme (light/dark) for the application.
 *
 * This hook handles:
 * - Reading the user's preferred color scheme
 * - Automatically switching between light and dark mode based on system preferences
 * - Updating the HTML document with the appropriate Bootstrap theme attribute
 *
 * @returns {ResolvedColorScheme} The current active color scheme ('light' or 'dark')
 *
 * @example
 * ```tsx
 * function App() {
 *   const colorScheme = useColorScheme();
 *
 *   return (
 *     <div>
 *       Current theme: {colorScheme}
 *     </div>
 *   );
 * }
 * ```
 */
export default function useColorScheme(): ResolvedColorScheme {
  const defaultColorScheme = usePreferredColorScheme();
  const [colorScheme, setColorScheme] = useState<ResolvedColorScheme>('light');

  const updateColorScheme = useCallback(
    (scheme: ResolvedColorScheme) => {
      // Update color scheme
      setColorScheme(scheme);

      // Also update HTML element's data-bs-theme attribute
      document.documentElement.dataset.bsTheme = scheme;
    },
    [setColorScheme]
  );

  useLayoutEffect(() => {
    let listener:
      | ((this: MediaQueryList, ev: MediaQueryListEvent) => any)
      | null = null;
    /**
     * Media query listener for detecting changes in the user's color scheme preference.
     */
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    if (defaultColorScheme === 'auto') {
      updateColorScheme(mediaQuery.matches ? 'dark' : 'light');

      listener = (e: MediaQueryListEvent) =>
        updateColorScheme(e.matches ? 'dark' : 'light');

      mediaQuery.addEventListener('change', listener);
    } else {
      // If the default color scheme is not 'auto', set it directly
      updateColorScheme(defaultColorScheme as 'dark' | 'light');
    }

    return () => {
      if (listener) mediaQuery.removeEventListener('change', listener);
    };
  }, [defaultColorScheme, updateColorScheme]);

  useDebugValue(
    colorScheme.concat(defaultColorScheme === 'auto' ? ' (auto)' : ' (forced)')
  );

  return colorScheme;
}
